import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8090/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
  const token = localStorage.getItem(tokenKey);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** Must match STAGE_COLUMN_MAP stage names in taxReturnTrackerService */
export const TAX_RETURN_NOTE_STATUSES = [
  'staging',
  'Need Additional Information',
  'In Progress',
  'In Review',
  'Ready',
  'Pending Confirmation',
  'Accepted',
  'Rejected',
] as const;

export type TaxReturnNoteStatus = (typeof TAX_RETURN_NOTE_STATUSES)[number];

export interface TaxReturnNoteStaff {
  id: string;
  name: string;
  firstname?: string;
  lastname?: string;
  preferredName?: string;
  role?: string;
  title?: string;
  designation?: string;
  team?: string;
  phone?: string;
  email?: string;
  initials: string;
}

export interface TaxReturnNote {
  id: string;
  redRockId: string;
  status: TaxReturnNoteStatus;
  notes: string;
  created: string;
  updated: string;
  staff: TaxReturnNoteStaff | null;
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function normalizeStatus(raw: unknown): TaxReturnNoteStatus | null {
  const value = String(raw || '').trim();
  const match = TAX_RETURN_NOTE_STATUSES.find(
    (s) => s.toLowerCase() === value.toLowerCase()
  );
  return match || null;
}

function relationId(raw: unknown): string {
  if (!raw) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object' && raw !== null && 'id' in raw) {
    return String((raw as { id: unknown }).id || '');
  }
  return String(raw);
}

function mapStaff(raw: any): TaxReturnNoteStaff | null {
  if (!raw || typeof raw !== 'object') return null;
  const firstname = String(raw.firstname || raw.first_name || '').trim();
  const lastname = String(raw.lastname || raw.last_name || '').trim();
  const preferredName = String(
    raw.preferredName || raw.preferred_name || ''
  ).trim();
  const name =
    preferredName ||
    raw.name ||
    raw.full_name ||
    raw.display_name ||
    [firstname, lastname].filter(Boolean).join(' ') ||
    raw.email ||
    raw.red_rock_email ||
    'Team member';

  return {
    id: String(raw.id || ''),
    name: String(name).trim() || 'Team member',
    firstname: firstname || undefined,
    lastname: lastname || undefined,
    preferredName: preferredName || undefined,
    role: String(raw.role || '').trim() || undefined,
    title: String(raw.title || raw.designation || '').trim() || undefined,
    designation: String(raw.designation || '').trim() || undefined,
    team: String(raw.team || '').trim() || undefined,
    phone:
      String(raw.phone || raw.red_rock_phone || raw.personal_phone || '').trim() ||
      undefined,
    email:
      String(raw.email || raw.red_rock_email || raw.personal_email || '').trim() ||
      undefined,
    initials: initialsFromName(String(name)),
  };
}

function mapNote(raw: any): TaxReturnNote | null {
  if (!raw || typeof raw !== 'object') return null;
  const status = normalizeStatus(raw.tax_return_status || raw.status);
  const notes = String(raw.notes || '').trim();
  if (!status || !notes) return null;

  const staff =
    mapStaff(raw.staff) ||
    mapStaff(raw.expand?.staff_v2) ||
    mapStaff(raw.staff_v2) ||
    mapStaff(raw.expand?.staff) ||
    null;

  return {
    id: String(raw.id || ''),
    redRockId: relationId(raw.redRockId || raw.red_rock_id || raw.expand?.red_rock_id),
    status,
    notes,
    created: String(raw.created || ''),
    updated: String(raw.updated || ''),
    staff,
  };
}

/**
 * Customer portal notes hook:
 * GET /api/tax-return-notes?redRockId=...
 * Implemented by: pb_hooks/client_tax_return_notes.pb.js
 */
export async function fetchTaxReturnNotes(redRockId: string): Promise<TaxReturnNote[]> {
  const id = String(redRockId || '').trim();
  if (!id) return [];

  try {
    const query = new URLSearchParams({ redRockId: id });
    const response = await api.get(`/tax-return-notes?${query.toString()}`);
    const data = response.data || {};

    if (data.success === false) {
      console.warn('[TaxReturnNotes] Hook returned success=false', data.error || data.message);
      return [];
    }

    const items = Array.isArray(data.items) ? data.items : [];
    return items.map(mapNote).filter(Boolean) as TaxReturnNote[];
  } catch (error: any) {
    console.warn(
      '[TaxReturnNotes] Hook GET /tax-return-notes failed',
      error?.response?.status,
      error?.response?.data || error?.message
    );
    return [];
  }
}

export function groupNotesByStatus(
  notes: TaxReturnNote[]
): Record<TaxReturnNoteStatus, TaxReturnNote[]> {
  const grouped = TAX_RETURN_NOTE_STATUSES.reduce(
    (acc, status) => {
      acc[status] = [];
      return acc;
    },
    {} as Record<TaxReturnNoteStatus, TaxReturnNote[]>
  );

  notes.forEach((note) => {
    grouped[note.status].push(note);
  });

  return grouped;
}

/** Portal view rule: only notes matching the tracker's current stage are shown. */
export function filterNotesByCurrentStage(
  notes: TaxReturnNote[],
  currentStageName: string | null | undefined
): TaxReturnNote[] {
  const stage = String(currentStageName || '').trim().toLowerCase();
  if (!stage) return [];
  return (notes || []).filter(
    (note) => String(note.status || '').trim().toLowerCase() === stage
  );
}

const taxReturnNotesService = {
  fetchTaxReturnNotes,
  groupNotesByStatus,
  filterNotesByCurrentStage,
};

export default taxReturnNotesService;
